import { useState, useEffect } from 'react';
import { X, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'android-app-banner-dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const AndroidAppBanner = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const ua = navigator.userAgent || '';

        // ONLY show on Android mobile browsers - not desktop, not iOS
        const isAndroid = /Android/i.test(ua);
        if (!isAndroid) return;

        // Detect if running inside the BrainSync native app (WebView adds "BrainSyncApp" to UA)
        if (/BrainSyncApp/i.test(ua)) return;

        // Suppress inside the iOS native shell too - iOS users don't
        // want an "Install our Android App" banner, and even iOS
        // users on regular mobile Safari get zero value from it. The
        // Android banner should only appear to Android/Chromebook
        // users on mobile Chrome/web. The `bs_is_ios` flag is
        // stamped by /app/ios-app/src/utils/injectedJS.ts.
        try {
            if (window.sessionStorage && window.sessionStorage.getItem('bs_is_ios') === '1') return;
        } catch (_) { /* private mode */ }
        if (/iPad|iPhone|iPod/.test(ua)) return;

        // Detect if running inside TWA, installed PWA, or standalone mode
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            || window.matchMedia('(display-mode: fullscreen)').matches
            || window.matchMedia('(display-mode: minimal-ui)').matches;

        // If already running as an installed app, never show
        if (isStandalone) return;

        // Check installed related apps API (available in Chrome on Android)
        if ('getInstalledRelatedApps' in navigator) {
            navigator.getInstalledRelatedApps().then(apps => {
                if (apps && apps.length > 0) return;
                showBannerIfNotDismissed();
            }).catch(() => showBannerIfNotDismissed());
        } else {
            showBannerIfNotDismissed();
        }

        function showBannerIfNotDismissed() {
            const dismissedAt = localStorage.getItem(DISMISS_KEY);
            if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_DURATION_MS) return;
            setVisible(true);
        }
    }, []);

    const dismiss = () => {
        setVisible(false);
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };

    if (!visible) return null;

    return (
        <div
            data-testid="android-app-banner"
            className="mx-3 mt-3 mb-1 rounded-2xl overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #1a2e1a 0%, #0f172a 100%)' }}
        >
            <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold leading-tight">Get the BrainSync App</p>
                    <p className="text-white/50 text-xs mt-0.5">Faster, native experience on Android</p>
                </div>
                <a
                    href="/brainsync-v1.3.4.apk"
                    data-testid="android-app-install-btn"
                    className="flex-shrink-0 px-4 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors"
                >
                    Install
                </a>
                <button
                    onClick={dismiss}
                    data-testid="android-app-banner-dismiss"
                    className="flex-shrink-0 text-white/30 hover:text-white/70 transition-colors p-0.5"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default AndroidAppBanner;
